import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Search,
  Package,
  ExternalLink,
  MapPin,
  Clock,
  CheckCircle2,
  Truck,
  ArrowRight,
  Calculator,
  Building2,
  Info,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { getCanadaPostTracking } from '@/features/shippings/api';
import { getCanadaPostTrackingUrl } from '@/features/shippings/types';
import type {
  CanadaPostTrackingResult,
  CanadaPostTrackingEvent,
} from '@/features/shippings/types';
import { api } from '@/lib/api';

// =============================================================================
// Tracking Tab
// =============================================================================

function getEventIcon(type: string) {
  const t = (type || '').toUpperCase();
  if (t === 'DELIVERY' || t === 'DELIVERED') return <CheckCircle2 className="h-4 w-4 text-green-600" />;
  if (t.includes('TRANSIT') || t === 'IN_TRANSIT') return <Truck className="h-4 w-4 text-blue-600" />;
  if (t === 'OUT_FOR_DELIVERY') return <ArrowRight className="h-4 w-4 text-yellow-600" />;
  return <Package className="h-4 w-4 text-gray-400" />;
}

function getStatusBadge(tracking: CanadaPostTrackingResult) {
  if (tracking.actualDeliveryDate) {
    return <Badge className="bg-green-100 text-green-800 hover:bg-green-100">Delivered</Badge>;
  }
  if (tracking.events.length > 0) {
    const latest = (tracking.events[0].type || tracking.events[0].description || '').toUpperCase();
    if (latest.includes('TRANSIT')) return <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100">In Transit</Badge>;
    if (latest.includes('OUT FOR DELIVERY')) return <Badge className="bg-yellow-100 text-yellow-800 hover:bg-yellow-100">Out for Delivery</Badge>;
    if (latest.includes('DELIVERED')) return <Badge className="bg-green-100 text-green-800 hover:bg-green-100">Delivered</Badge>;
  }
  return <Badge className="bg-gray-100 text-gray-800 hover:bg-gray-100">Pending</Badge>;
}

function fmtDateTime(dateStr: string | null): string {
  if (!dateStr) return '--';
  try {
    return new Date(dateStr).toLocaleString('en-CA', {
      timeZone: 'America/Montreal', month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return dateStr; }
}

function fmtDate(dateStr: string | null): string {
  if (!dateStr) return '--';
  try {
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-CA', {
      timeZone: 'America/Montreal', month: 'long', day: 'numeric', year: 'numeric',
    });
  } catch { return dateStr; }
}

function TrackingCard({ tracking }: { tracking: CanadaPostTrackingResult }) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CardTitle className="font-mono text-lg">{tracking.pin}</CardTitle>
            {getStatusBadge(tracking)}
          </div>
          <Button variant="outline" size="sm" asChild>
            <a href={getCanadaPostTrackingUrl(tracking.pin)} target="_blank" rel="noopener noreferrer">
              <ExternalLink className="h-4 w-4 mr-1" /> Canada Post
            </a>
          </Button>
        </div>
        <div className="flex gap-6 text-sm text-muted-foreground mt-1">
          {tracking.serviceName && <span>{tracking.serviceName}</span>}
          {tracking.actualDeliveryDate ? (
            <span className="text-green-700 font-medium">Delivered {fmtDate(tracking.actualDeliveryDate)}</span>
          ) : tracking.expectedDeliveryDate ? (
            <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> Expected {fmtDate(tracking.expectedDeliveryDate)}</span>
          ) : null}
        </div>
      </CardHeader>
      <CardContent>
        {tracking.events.length === 0 ? (
          <p className="text-muted-foreground text-sm py-4 text-center">No tracking events available</p>
        ) : (
          <div className="relative pl-8">
            <div className="absolute left-[15px] top-2 bottom-2 w-px bg-border" />
            {tracking.events.map((event: CanadaPostTrackingEvent, index: number) => (
              <div key={index} className="relative pb-5 last:pb-0">
                <div className="absolute left-[-22px] top-0.5 bg-background p-0.5">{getEventIcon(event.type)}</div>
                <div>
                  <div className="text-sm font-medium">{event.description}</div>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5">
                    <span>{fmtDateTime(event.date)}</span>
                    {event.location && (
                      <span className="flex items-center gap-0.5"><MapPin className="h-3 w-3" />{event.location}</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function TrackingTab() {
  const [input, setInput] = useState('');
  const [pins, setPins] = useState<string[]>([]);

  const trackingQuery = useQuery({
    queryKey: ['canada-post-tracking-lookup', pins],
    queryFn: () => getCanadaPostTracking(pins),
    enabled: pins.length > 0,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = input.split(/[\s,;]+/).map((s) => s.trim().replace(/\s/g, '')).filter(Boolean);
    if (parsed.length > 0) setPins(parsed);
  };

  const results = trackingQuery.data || [];

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="pt-6">
          <form onSubmit={handleSearch} className="flex gap-3">
            <Input
              placeholder="Enter tracking number(s) — separate multiple with commas"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              className="flex-1"
            />
            <Button type="submit" disabled={!input.trim() || trackingQuery.isFetching}>
              <Search className="h-4 w-4 mr-1" /> Track
            </Button>
          </form>
        </CardContent>
      </Card>

      {trackingQuery.isFetching && (
        <div className="space-y-4">
          {pins.map((pin) => (
            <Card key={pin}>
              <CardHeader><Skeleton className="h-6 w-48" /><Skeleton className="h-4 w-32 mt-2" /></CardHeader>
              <CardContent><div className="space-y-3">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-12 w-full" />)}</div></CardContent>
            </Card>
          ))}
        </div>
      )}

      {!trackingQuery.isFetching && results.length > 0 && (
        <div className="space-y-4">{results.map((t) => <TrackingCard key={t.pin} tracking={t} />)}</div>
      )}

      {!trackingQuery.isFetching && pins.length > 0 && results.length === 0 && !trackingQuery.error && (
        <Card>
          <CardContent className="py-12 text-center">
            <Package className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
            <p className="text-muted-foreground">No tracking results found for the provided PIN(s).</p>
          </CardContent>
        </Card>
      )}

      {trackingQuery.error && (
        <Card><CardContent className="py-8 text-center"><p className="text-red-500">Error loading tracking data. Please try again.</p></CardContent></Card>
      )}

      {pins.length === 0 && (
        <Card>
          <CardContent className="py-16 text-center">
            <Search className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
            <p className="text-muted-foreground">Enter a Canada Post tracking number above to look up delivery status.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// =============================================================================
// Rate Calculator Tab
// =============================================================================

interface RateResult {
  serviceCode: string;
  serviceName: string;
  due: string;
  expectedTransitTime: string | null;
  expectedDeliveryDate: string | null;
  guaranteedDelivery: boolean;
}

function RateCalculatorTab() {
  const [originPostal, setOriginPostal] = useState('H4R 2K9');
  const [destPostal, setDestPostal] = useState('');
  const [destType, setDestType] = useState<'domestic' | 'us' | 'international'>('domestic');
  const [destCountry, setDestCountry] = useState('US');
  const [weight, setWeight] = useState('1');
  const [length, setLength] = useState('');
  const [width, setWidth] = useState('');
  const [height, setHeight] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const queryParams = useMemo(() => {
    if (!submitted) return null;
    const w = parseFloat(weight);
    if (!originPostal || !w || w <= 0) return null;

    const params: Record<string, string | number> = {
      originPostalCode: originPostal.replace(/\s/g, ''),
      weight: w,
    };

    if (destType === 'domestic') {
      if (!destPostal) return null;
      params.destPostalCode = destPostal.replace(/\s/g, '');
    } else if (destType === 'us') {
      if (!destPostal) return null;
      params.destZipCode = destPostal;
    } else {
      if (!destCountry) return null;
      params.destCountryCode = destCountry;
      if (destPostal) params.destPostalCode = destPostal;
    }

    const l = parseFloat(length), wi = parseFloat(width), h = parseFloat(height);
    if (l > 0 && wi > 0 && h > 0) {
      params.length = l;
      params.width = wi;
      params.height = h;
    }
    return params;
  }, [submitted, originPostal, destPostal, destType, destCountry, weight, length, width, height]);

  const ratesQuery = useQuery<RateResult[]>({
    queryKey: ['canada-post-rates', queryParams],
    queryFn: async () => {
      if (!queryParams) return [];
      return api.get<RateResult[]>('/v4/packing-slips/rates/canada-post', queryParams);
    },
    enabled: !!queryParams,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="pt-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <Label>Origin Postal Code</Label>
                <Input value={originPostal} onChange={(e) => { setOriginPostal(e.target.value); setSubmitted(false); }} placeholder="H4R 2K9" />
              </div>
              <div>
                <Label>Destination Type</Label>
                <Select value={destType} onValueChange={(v) => { setDestType(v as typeof destType); setDestPostal(''); setSubmitted(false); }}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="domestic">Canada</SelectItem>
                    <SelectItem value="us">United States</SelectItem>
                    <SelectItem value="international">International</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {destType === 'international' && (
                <div>
                  <Label>Country Code</Label>
                  <Input value={destCountry} onChange={(e) => { setDestCountry(e.target.value.toUpperCase()); setSubmitted(false); }} placeholder="FR" maxLength={2} />
                </div>
              )}
              <div>
                <Label>{destType === 'us' ? 'ZIP Code' : 'Postal Code'}</Label>
                <Input value={destPostal} onChange={(e) => { setDestPostal(e.target.value); setSubmitted(false); }} placeholder={destType === 'us' ? '10001' : 'M5V 2T6'} />
              </div>
              <div>
                <Label>Weight (kg)</Label>
                <Input type="number" step="0.1" min="0.01" value={weight} onChange={(e) => { setWeight(e.target.value); setSubmitted(false); }} />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Length (cm)</Label>
                <Input type="number" step="0.1" value={length} onChange={(e) => { setLength(e.target.value); setSubmitted(false); }} placeholder="Optional" />
              </div>
              <div>
                <Label>Width (cm)</Label>
                <Input type="number" step="0.1" value={width} onChange={(e) => { setWidth(e.target.value); setSubmitted(false); }} placeholder="Optional" />
              </div>
              <div>
                <Label>Height (cm)</Label>
                <Input type="number" step="0.1" value={height} onChange={(e) => { setHeight(e.target.value); setSubmitted(false); }} placeholder="Optional" />
              </div>
            </div>

            <Button type="submit" disabled={ratesQuery.isFetching}>
              <Calculator className="h-4 w-4 mr-1" /> Get Rates
            </Button>
          </form>
        </CardContent>
      </Card>

      {ratesQuery.isFetching && (
        <Card><CardContent className="py-6"><div className="space-y-3">{[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-16 w-full" />)}</div></CardContent></Card>
      )}

      {ratesQuery.error && (
        <Card><CardContent className="py-8 text-center"><p className="text-red-500">Error fetching rates: {(ratesQuery.error as Error).message}</p></CardContent></Card>
      )}

      {!ratesQuery.isFetching && ratesQuery.data && ratesQuery.data.length > 0 && (
        <div className="grid gap-3">
          {ratesQuery.data.map((rate) => (
            <Card key={rate.serviceCode}>
              <CardContent className="py-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium">{rate.serviceName}</div>
                    <div className="text-sm text-muted-foreground flex items-center gap-3">
                      <span className="font-mono">{rate.serviceCode}</span>
                      {rate.expectedTransitTime && <span>{rate.expectedTransitTime} business day{rate.expectedTransitTime !== '1' ? 's' : ''}</span>}
                      {rate.expectedDeliveryDate && <span>Est. {fmtDate(rate.expectedDeliveryDate)}</span>}
                      {rate.guaranteedDelivery && <Badge variant="outline" className="text-xs">Guaranteed</Badge>}
                    </div>
                  </div>
                  <div className="text-2xl font-bold tabular-nums">${rate.due}</div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {!ratesQuery.isFetching && ratesQuery.data && ratesQuery.data.length === 0 && submitted && (
        <Card><CardContent className="py-8 text-center"><p className="text-muted-foreground">No rates available for this route.</p></CardContent></Card>
      )}

      {!submitted && (
        <Card>
          <CardContent className="py-16 text-center">
            <Calculator className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
            <p className="text-muted-foreground">Enter origin, destination and weight to get shipping rates.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// =============================================================================
// Find Post Office Tab
// =============================================================================

interface PostOfficeResult {
  officeId: string;
  name: string;
  location: string;
  distance: number;
  address: {
    officeAddress: string;
    city: string;
    province: string;
    postalCode: string;
    latitude: number;
    longitude: number;
  };
}

function FindPostOfficeTab() {
  const [postalCode, setPostalCode] = useState('H4R 2K9');
  const [maxResults, setMaxResults] = useState('10');
  const [submitted, setSubmitted] = useState(false);

  const queryParams = useMemo(() => {
    if (!submitted || !postalCode.trim()) return null;
    return { postalCode: postalCode.replace(/\s/g, ''), maximum: parseInt(maxResults, 10) || 10 };
  }, [submitted, postalCode, maxResults]);

  const officesQuery = useQuery<PostOfficeResult[]>({
    queryKey: ['canada-post-offices', queryParams],
    queryFn: () => api.get<PostOfficeResult[]>('/v4/packing-slips/post-offices/canada-post', queryParams!),
    enabled: !!queryParams,
    staleTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="pt-6">
          <form onSubmit={handleSubmit} className="flex items-end gap-3">
            <div>
              <Label>Postal Code</Label>
              <Input
                value={postalCode}
                onChange={(e) => { setPostalCode(e.target.value); setSubmitted(false); }}
                placeholder="H4R 2K9"
                className="w-40"
              />
            </div>
            <div>
              <Label>Max Results</Label>
              <Input
                type="number"
                min="1"
                max="50"
                value={maxResults}
                onChange={(e) => { setMaxResults(e.target.value); setSubmitted(false); }}
                className="w-24"
              />
            </div>
            <Button type="submit" disabled={!postalCode.trim() || officesQuery.isFetching}>
              <Search className="h-4 w-4 mr-1" /> Find
            </Button>
          </form>
        </CardContent>
      </Card>

      {officesQuery.isFetching && (
        <div className="space-y-3">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-24 w-full" />)}</div>
      )}

      {officesQuery.error && (
        <Card><CardContent className="py-8 text-center"><p className="text-red-500">Error finding post offices.</p></CardContent></Card>
      )}

      {!officesQuery.isFetching && officesQuery.data && officesQuery.data.length > 0 && (
        <div className="grid gap-3">
          {officesQuery.data.map((office) => (
            <Card key={office.officeId}>
              <CardContent className="py-4">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="font-medium">{office.name}</div>
                    <div className="text-sm text-muted-foreground">{office.location}</div>
                    <div className="text-sm mt-1 flex items-center gap-1">
                      <MapPin className="h-3 w-3" />
                      {office.address.officeAddress}, {office.address.city}, {office.address.province} {office.address.postalCode}
                    </div>
                  </div>
                  <Badge variant="outline" className="text-sm tabular-nums shrink-0">
                    {office.distance} km
                  </Badge>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {!officesQuery.isFetching && officesQuery.data && officesQuery.data.length === 0 && submitted && (
        <Card><CardContent className="py-8 text-center"><p className="text-muted-foreground">No post offices found.</p></CardContent></Card>
      )}

      {!submitted && (
        <Card>
          <CardContent className="py-16 text-center">
            <Building2 className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
            <p className="text-muted-foreground">Enter a postal code to find nearby Canada Post offices.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// =============================================================================
// Account Info Tab
// =============================================================================

function AccountTab() {
  const servicesQuery = useQuery<{ serviceCode: string; serviceName: string }[]>({
    queryKey: ['canada-post-services-account'],
    queryFn: () => api.get('/v4/packing-slips/rates/canada-post', {
      originPostalCode: 'H4R2K9',
      destPostalCode: 'M5V2T6',
      weight: 1,
    }),
    staleTime: 10 * 60 * 1000,
  });

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Account Details</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-y-3 gap-x-8 text-sm">
            <div>
              <span className="text-muted-foreground">Customer Number</span>
              <p className="font-mono font-medium">0009713179</p>
            </div>
            <div>
              <span className="text-muted-foreground">Account Type</span>
              <p className="font-medium">Small/Medium Business (SMB)</p>
            </div>
            <div>
              <span className="text-muted-foreground">Company</span>
              <p className="font-medium">ATEK</p>
            </div>
            <div>
              <span className="text-muted-foreground">Contact</span>
              <p className="font-medium">Hakim Rouab</p>
            </div>
            <div>
              <span className="text-muted-foreground">Return Address</span>
              <p className="font-medium">5490 Boulevard Thimens, Suite 240</p>
              <p className="text-muted-foreground">Saint-Laurent, QC H4R 2K9</p>
            </div>
            <div>
              <span className="text-muted-foreground">Environment</span>
              <p className="font-medium">Production</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>API Capabilities</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {[
              { name: 'Tracking', desc: 'Track any PIN', active: true },
              { name: 'Rating', desc: 'Get shipping rates', active: true },
              { name: 'Non-Contract Shipping', desc: 'Create shipments + labels', active: true },
              { name: 'Authorized Returns', desc: 'Generate return labels (RMA)', active: true },
              { name: 'Find Post Office', desc: 'Nearest locations', active: true },
              { name: 'Contract Shipping', desc: 'Manifest-based (requires upgrade)', active: false },
            ].map((cap) => (
              <div key={cap.name} className={`border rounded-lg p-3 ${cap.active ? '' : 'opacity-50'}`}>
                <div className="flex items-center gap-2">
                  {cap.active ? (
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                  ) : (
                    <Clock className="h-4 w-4 text-muted-foreground" />
                  )}
                  <span className="text-sm font-medium">{cap.name}</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">{cap.desc}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {servicesQuery.data && servicesQuery.data.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Available Services (Montreal to Toronto)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {servicesQuery.data.map((rate) => (
                <div key={rate.serviceCode} className="border rounded-lg p-3">
                  <div className="text-sm font-medium">{rate.serviceName}</div>
                  <div className="text-xs font-mono text-muted-foreground">{rate.serviceCode}</div>
                  <div className="text-lg font-bold mt-1">${(rate as Record<string, unknown>).due as string}</div>
                  {(rate as Record<string, unknown>).expectedTransitTime ? (
                    <div className="text-xs text-muted-foreground">
                      {String((rate as Record<string, unknown>).expectedTransitTime)} business day(s)
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// =============================================================================
// Main Page
// =============================================================================

export default function CanadaPostPage() {
  return (
    <div className="space-y-5">
      <div>
        <h1 className="page-title">
          Canada Post <small>Tools & Services</small>
        </h1>
      </div>

      <Tabs defaultValue="tracking" className="space-y-4">
        <TabsList>
          <TabsTrigger value="tracking">
            <Search className="h-4 w-4 mr-1.5" /> Tracking
          </TabsTrigger>
          <TabsTrigger value="rates">
            <Calculator className="h-4 w-4 mr-1.5" /> Rates
          </TabsTrigger>
          <TabsTrigger value="post-offices">
            <Building2 className="h-4 w-4 mr-1.5" /> Post Offices
          </TabsTrigger>
          <TabsTrigger value="account">
            <Info className="h-4 w-4 mr-1.5" /> Account
          </TabsTrigger>
        </TabsList>

        <TabsContent value="tracking">
          <TrackingTab />
        </TabsContent>

        <TabsContent value="rates">
          <RateCalculatorTab />
        </TabsContent>

        <TabsContent value="post-offices">
          <FindPostOfficeTab />
        </TabsContent>

        <TabsContent value="account">
          <AccountTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
